import { API_BASE_URL } from '@types';
import * as XLSX from 'xlsx';
import {
  buildV0ContactsExcelWorkbook,
  v0ContactsExcelFilename,
} from '../admin/legacyV0/buildV0UserExcel';

export type V0MigrationStatus =
  | 'pending'
  | 'ignored'
  | 'dry_run'
  | 'migrated'
  | 'failed';

export type V0LegacyMigrationInfo = {
  status: V0MigrationStatus;
  note: string | null;
  updated_at: string | null;
  new_user_id: number | null;
};

export type V0LegacyUserListItem = {
  id: number;
  post_id: number;
  display_name: string;
  email: string | null;
  email_ok: boolean;
  email_is_fallback?: boolean;
  utente_wp: number | null;
  tipo_cliente: string | null;
  citta: string | null;
  stato: string | null;
  lingua: string | null;
  abbonamenti_count: number;
  oculus_count: number;
  migration: V0LegacyMigrationInfo;
};

export type V0LegacyUsersListResponse = {
  users: V0LegacyUserListItem[];
  pagination: {
    currentPage: number;
    pageSize: number;
    totalRecords: number;
    totalPages: number;
  };
  import_meta?: Record<string, unknown> | null;
};

export type V0LegacyUserDetail = {
  vapor_source?: { database: string; note: string };
  cliente: Record<string, unknown>;
  wp_user: Record<string, unknown> | null;
  usermeta: Record<string, string | null>;
  abbonamenti: Array<
    Record<string, unknown> & {
      product_remap?: {
        action: 'map' | 'skip';
        be_product_id: number | null;
        be_product_name: string | null;
        note?: string;
      };
    }
  >;
  oculus: Record<string, unknown>[];
  email_ok: boolean;
  email_is_fallback?: boolean;
  email: string | null;
  field_map?: Array<{ source: string; target: string; required?: boolean }>;
  product_map?: Array<{ source: string; target: string; required?: boolean }>;
  abbonamenti_summary?: { total: number; mapped: number; skipped: number };
  migration: V0LegacyMigrationInfo;
};

export type LegacyV0UserMigrationExportPayload = {
  schema_version: number;
  /** Shared with v1; old files may still use legacy_v0_user_migration_export. */
  kind: 'legacy_user_migration_export' | 'legacy_v0_user_migration_export';
  legacy_source?: 'v0';
  exported_at: string;
  legacy_user_id: number;
  email: string | null;
  email_ok: boolean;
  email_is_fallback?: boolean;
  source: Record<string, unknown>;
  catalog: Record<string, unknown>;
};

const parseApiError = async (response: Response, fallback: string): Promise<string> => {
  const text = await response.text();
  let message = text || fallback;
  try {
    const parsed = JSON.parse(text) as { message?: string };
    if (parsed.message) message = parsed.message;
  } catch {
    // keep
  }
  return message;
};

export const fetchLegacyV0Users = async (
  page: number = 1,
  pageSize: number = 25,
  query?: string,
  migrationStatus: 'all' | 'pending' | 'ignored' = 'all',
  email: 'all' | 'ok' | 'missing' = 'all'
): Promise<V0LegacyUsersListResponse> => {
  let url = `${API_BASE_URL}/services/v1/admin/legacy-v0-users?page=${page}&size=${pageSize}`;
  if (query?.trim()) url += `&query=${encodeURIComponent(query.trim())}`;
  if (migrationStatus !== 'all') url += `&migrationStatus=${migrationStatus}`;
  if (email !== 'all') url += `&email=${email}`;

  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!response.ok) {
    throw new Error(await parseApiError(response, `Failed to list v0 users (${response.status})`));
  }
  return response.json();
};

export const fetchLegacyV0User = async (userId: number): Promise<V0LegacyUserDetail> => {
  const response = await fetch(
    `${API_BASE_URL}/services/v1/admin/legacy-v0-users/${userId}`,
    {
      method: 'GET',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    }
  );
  if (!response.ok) {
    throw new Error(await parseApiError(response, `Failed to fetch v0 user (${response.status})`));
  }
  return response.json();
};

export const updateLegacyV0UserMigrationStatus = async (
  userId: number,
  status: V0MigrationStatus,
  note: string | null = null
): Promise<void> => {
  const response = await fetch(
    `${API_BASE_URL}/services/v1/admin/legacy-v0-users/${userId}/migration-status`,
    {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status, note }),
    }
  );
  if (!response.ok) {
    throw new Error(await parseApiError(response, `Failed to update status (${response.status})`));
  }
};

/** Bulk: all migrated → pending (be2025 data kept). */
export const resetMigratedLegacyV0Users = async (): Promise<{
  reset: number;
  legacy_user_ids: number[];
}> => {
  const response = await fetch(
    `${API_BASE_URL}/services/v1/admin/legacy-v0-users/reset-migrated`,
    {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    }
  );
  if (!response.ok) {
    throw new Error(
      await parseApiError(response, `Failed to reset migrated v0 users (${response.status})`)
    );
  }
  return response.json();
};

export const exportLegacyV0UserMigrationJson = async (
  userId: number
): Promise<LegacyV0UserMigrationExportPayload> => {
  const response = await fetch(
    `${API_BASE_URL}/services/v1/admin/legacy-v0-users/${userId}/export-migration-json`,
    {
      method: 'GET',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    }
  );
  if (!response.ok) {
    throw new Error(await parseApiError(response, `Failed to export v0 JSON (${response.status})`));
  }
  return response.json();
};

/** Batch-check which emails already exist in be2025 Users. */
export const checkV0EmailsInBe2025 = async (
  emails: string[]
): Promise<Set<string>> => {
  const unique = [
    ...new Set(
      emails
        .map((e) => (e || '').trim().toLowerCase())
        .filter(Boolean)
    ),
  ];
  if (unique.length === 0) return new Set();

  const response = await fetch(
    `${API_BASE_URL}/services/v1/admin/legacy-v0-users/emails-in-be2025`,
    {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ emails: unique }),
    }
  );
  if (!response.ok) {
    throw new Error(
      await parseApiError(response, `Failed to check emails in be2025 (${response.status})`)
    );
  }
  const result = (await response.json()) as { present?: string[] };
  return new Set((result.present ?? []).map((e) => e.toLowerCase()));
};

/** Best-effort: missing/undeployed endpoint must not block Excel download. */
const resolvePresentEmails = async (emails: string[]): Promise<Set<string>> => {
  try {
    return await checkV0EmailsInBe2025(emails);
  } catch (err) {
    console.warn('[v0 excel] emails-in-be2025 unavailable; in_be2025 will be no', err);
    return new Set();
  }
};

const downloadXlsxWorkbook = (workbook: XLSX.WorkBook, filename: string): void => {
  const buffer = XLSX.write(workbook, {
    bookType: 'xlsx',
    type: 'array',
  }) as ArrayBuffer;
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
};

export const v0MigrationExportFilename = (
  payload: Pick<LegacyV0UserMigrationExportPayload, 'legacy_user_id' | 'email'>
): string => {
  const emailSafe = (payload.email || 'no-email').replace(/[^a-z0-9._-]+/gi, '_');
  return `legacy-v0-migration-${payload.legacy_user_id}-${emailSafe}.json`;
};

export const v0MigrationExcelFilename = (
  payload: Pick<LegacyV0UserMigrationExportPayload, 'legacy_user_id' | 'email'>
): string => {
  const emailSafe = (payload.email || 'no-email').replace(/[^a-z0-9._-]+/gi, '_');
  return `legacy-v0-migration-${payload.legacy_user_id}-${emailSafe}.xlsx`;
};

export const saveLegacyV0UserMigrationExport = async (
  payload: LegacyV0UserMigrationExportPayload
): Promise<{ mode: 'filesystem'; path: string }> => {
  const filename = v0MigrationExportFilename(payload);
  const relativePath = `migration/v0/${filename}`;

  let response: Response;
  try {
    response = await fetch('/__dev/migration-v0-export', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ filename, payload }),
    });
  } catch (err) {
    throw new Error(
      `Cannot write ${relativePath}: Vite dev middleware /__dev/migration-v0-export unreachable. Run yarn/npm dev locally (no browser download fallback). ${
        err instanceof Error ? err.message : ''
      }`
    );
  }

  if (!response.ok) {
    const text = await response.text();
    let message = text || `Failed to write ${relativePath} (${response.status})`;
    try {
      const parsed = JSON.parse(text) as { message?: string };
      if (parsed.message) message = parsed.message;
    } catch {
      // keep
    }
    throw new Error(message);
  }

  const result = (await response.json()) as { path?: string };
  return { mode: 'filesystem', path: result.path || relativePath };
};

/** Browser download: one sheet with a single contact row. */
export const downloadLegacyV0UserExcel = async (
  userId: number
): Promise<{ filename: string }> => {
  const payload = await exportLegacyV0UserMigrationJson(userId);
  const presentEmails = await resolvePresentEmails(
    payload.email ? [payload.email] : []
  );
  const filename = v0MigrationExcelFilename(payload)
    .replace(/\.json$/, '.xlsx')
    .replace('legacy-v0-migration-', 'legacy-v0-contact-');
  const workbook = buildV0ContactsExcelWorkbook([payload], presentEmails);
  downloadXlsxWorkbook(workbook, filename);
  return { filename };
};

/** One Excel download with all filtered contacts on a single sheet. */
export const generateAllLegacyV0MigrationExcelData = async (options?: {
  migrationStatus?: 'all' | 'pending' | 'ignored';
  email?: 'all' | 'ok' | 'missing';
  onProgress?: (info: {
    done: number;
    total: number;
    userId?: number;
    ok: boolean;
    filename?: string;
    error?: string;
  }) => void;
}): Promise<{
  total: number;
  downloaded: number;
  filename: string | null;
  failed: Array<{ userId: number; error: string }>;
}> => {
  const migrationStatus = options?.migrationStatus ?? 'pending';
  const email = options?.email ?? 'ok';
  const pageSize = 100;
  let page = 1;
  let totalPages = 1;
  const users: Array<{ id: number }> = [];

  while (page <= totalPages) {
    const batch = await fetchLegacyV0Users(page, pageSize, undefined, migrationStatus, email);
    totalPages = Math.max(1, batch.pagination.totalPages);
    for (const user of batch.users) users.push({ id: user.id });
    page += 1;
  }

  const payloads: LegacyV0UserMigrationExportPayload[] = [];
  const failed: Array<{ userId: number; error: string }> = [];

  for (let i = 0; i < users.length; i += 1) {
    const user = users[i];
    try {
      const payload = await exportLegacyV0UserMigrationJson(user.id);
      payloads.push(payload);
      options?.onProgress?.({
        done: i + 1,
        total: users.length,
        userId: user.id,
        ok: true,
      });
    } catch (err) {
      const error = err instanceof Error ? err.message : 'Export failed';
      failed.push({ userId: user.id, error });
      options?.onProgress?.({
        done: i + 1,
        total: users.length,
        userId: user.id,
        ok: false,
        error,
      });
    }
  }

  if (payloads.length === 0) {
    return { total: users.length, downloaded: 0, filename: null, failed };
  }

  const presentEmails = await resolvePresentEmails(
    payloads.map((p) => p.email || '')
  );
  const filename = v0ContactsExcelFilename();
  const workbook = buildV0ContactsExcelWorkbook(payloads, presentEmails);
  downloadXlsxWorkbook(workbook, filename);
  options?.onProgress?.({
    done: users.length,
    total: users.length,
    ok: true,
    filename,
  });

  return {
    total: users.length,
    downloaded: 1,
    filename,
    failed,
  };
};

export const migrateLegacyV0UserFromJson = async (
  payload: LegacyV0UserMigrationExportPayload,
  options?: { dryRun?: boolean }
): Promise<{
  dryRun: boolean;
  migration?: {
    status: string;
    note: string | null;
    updated_at: string | null;
    new_user_id: number | null;
  };
  applied?: {
    new_user_id: number;
    created_user: boolean;
    products_upserted: number;
    devices_created: number;
    devices_existing: number;
    registration_create_time?: string | null;
  };
  report?: Record<string, unknown>;
  plan?: Record<string, unknown>;
}> => {
  const dryRun = options?.dryRun ? 'true' : 'false';
  const response = await fetch(
    `${API_BASE_URL}/services/v1/admin/legacy-v0-users/migrate-from-json?dryRun=${dryRun}`,
    {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }
  );
  if (!response.ok) {
    throw new Error(await parseApiError(response, `Failed to migrate v0 from JSON (${response.status})`));
  }
  return response.json();
};

export const generateAllLegacyV0MigrationJsonData = async (options?: {
  migrationStatus?: 'all' | 'pending' | 'ignored';
  email?: 'all' | 'ok' | 'missing';
  onProgress?: (info: {
    done: number;
    total: number;
    userId: number;
    ok: boolean;
    path?: string;
    error?: string;
  }) => void;
}): Promise<{
  total: number;
  saved: number;
  failed: Array<{ userId: number; error: string }>;
}> => {
  const migrationStatus = options?.migrationStatus ?? 'pending';
  const email = options?.email ?? 'ok';
  const pageSize = 100;
  let page = 1;
  let totalPages = 1;
  const users: Array<{ id: number }> = [];

  while (page <= totalPages) {
    const batch = await fetchLegacyV0Users(page, pageSize, undefined, migrationStatus, email);
    totalPages = Math.max(1, batch.pagination.totalPages);
    for (const user of batch.users) users.push({ id: user.id });
    page += 1;
  }

  const failed: Array<{ userId: number; error: string }> = [];
  let saved = 0;
  for (let i = 0; i < users.length; i += 1) {
    const user = users[i];
    try {
      const payload = await exportLegacyV0UserMigrationJson(user.id);
      const result = await saveLegacyV0UserMigrationExport(payload);
      saved += 1;
      options?.onProgress?.({
        done: i + 1,
        total: users.length,
        userId: user.id,
        ok: true,
        path: result.path,
      });
    } catch (err) {
      const error = err instanceof Error ? err.message : 'Export failed';
      failed.push({ userId: user.id, error });
      options?.onProgress?.({
        done: i + 1,
        total: users.length,
        userId: user.id,
        ok: false,
        error,
      });
    }
  }
  return { total: users.length, saved, failed };
};
